import React from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { pageFromLocation } from '@/utils/documentPreview';
import { useDocumentPage } from './DocumentPageContext';

interface EvidenceProps {
  quote?: string;
  location?: string;
  verified: boolean;
  className?: string;
}

/**
 * The model's quote and where it points (spec 6.3). The quote, page and confidence come from a model
 * that read a document which may have been written to mislead it, so the quote is rendered as plain
 * React text (never HTML or Markdown), and the label says whether our own check found it in the
 * document text. Either way the reviewer decides whether the value follows from it. On a page that
 * can show the document the label jumps to the page it names, also for an unverified quote: that is
 * where the reviewer most needs to look.
 */
export const Evidence: React.FC<EvidenceProps> = ({ quote, location, verified, className }) => {
  const showPage = useDocumentPage();
  if (!quote && !location) return null;
  const page = showPage ? pageFromLocation(location) : null;
  const where = location ? (location.startsWith('part ') ? `in ${location}` : `on ${location}`) : 'in the document text';
  const label = verified
    ? `Quote found ${where}`
    : `Quote not found in the document text${location ? ` (model cites ${location})` : ''}`;
  return (
    <div className={cn('space-y-1', className)}>
      {quote && (
        <blockquote className="border-l-2 border-grey-300 pl-2 text-xs text-grey-600 italic break-words whitespace-pre-wrap">
          {quote}
        </blockquote>
      )}
      <p
        className={cn('flex items-center gap-1 text-xs', verified ? 'text-success-dark' : 'text-warning-dark')}
        data-testid="evidence-label"
      >
        {verified ? (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        {showPage && page !== null ? (
          <button
            type="button"
            className="text-left underline decoration-dotted underline-offset-2 hover:decoration-solid"
            aria-label={`Show page ${page} of the document. ${label}`}
            data-testid="evidence-page"
            onClick={() => showPage(page)}
          >
            {label}
          </button>
        ) : (
          label
        )}
      </p>
    </div>
  );
};
